/* eslint-disable no-restricted-syntax, no-continue, no-console */
import Decimal from 'decimal.js'
import { createSelector } from 'reselect'
import { isCrypto, isFiat } from '@sparling/financial-common'
import { RootState } from '../types'
import { userActiveWalletsSelector, userBaseCurrencySelector } from './users'

export const isBalancesLoadingSelector = (state: RootState): boolean => {
  return state.balances.status === 'LOADING'
}

export const balancesSelector = (state: RootState): RootState['balances'] => {
  return state.balances
}

export const balancesWithNonZeroAmountSelector = createSelector(balancesSelector, (balances) =>
  balances.data.filter((b) => {
    try {
      const amount = new Decimal(b.amount)
      return amount.isFinite() && !amount.isZero()
    } catch (e) {
      console.error(`Failed to parse balance amount for asset ${b.asset}: ${b.amount}`)
      return false
    }
  }),
)

export const availableListForSettlementRequestSelector = createSelector(
  balancesWithNonZeroAmountSelector,
  userActiveWalletsSelector,
  (balances, wallets) =>
    balances
      .filter((balance) => isFiat(balance.asset) || wallets?.find((wallet) => wallet.currency === balance.asset))
      .map((balance) => ({
        label: balance.asset,
        value: balance.asset,
        address: wallets.find((wallet) => wallet.currency === balance.asset)?.address,
      })),
)

export const cryptoBalancesSelector = createSelector(balancesSelector, (balances) =>
  balances.data.filter((b) => isCrypto(b.asset)),
)

export const fiatBalancesSelector = createSelector(balancesSelector, (balances) =>
  balances.data.filter((b) => isFiat(b.asset)),
)

export const makeQuoteCurrencyBalanceSelector = (): ((state: RootState, quoteCurrency: string) => number) =>
  createSelector(
    balancesSelector,
    (_state: RootState, quoteCurrency: string | undefined) => quoteCurrency,
    (balances, quoteCurrency) => {
      if (!balances.data || !quoteCurrency) {
        return 0
      }
      const balance = balances.data.find((b) => b.asset === quoteCurrency)
      return balance ? parseFloat(balance.amount) : 0
    },
  )

export const userEquitySelector = createSelector(
  userBaseCurrencySelector,
  balancesSelector,
  (state: RootState): RootState['instruments']['tradableInstruments']['data'] =>
    state.instruments.tradableInstruments.data,
  (userCurrency, balances, instruments): string => {
    if (!userCurrency || !instruments?.length) return '-'
    if (!balances?.data?.length)
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: userCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(0)

    let totalEquity = new Decimal(0)

    const ratesMap = new Map<string, { bid: Decimal; ask: Decimal }>()

    instruments.forEach((i) => {
      if (i.type === 'SPOT' && typeof i.bid === 'number' && typeof i.ask === 'number') {
        const match = i.name.match(/^([A-Z]+)([A-Z]+)\.SPOT$/)
        if (match) {
          const [, base, quote] = match
          ratesMap.set(base + quote, { bid: new Decimal(i.bid), ask: new Decimal(i.ask) })
        }
      }
    })

    const bidsMap = new Map<string, number>()
    instruments.forEach((i) => {
      if (i.type === 'SPOT' && typeof i.bid === 'number') {
        bidsMap.set(i.underlier, i.bid)
      }
    })

    const findConversionRate = (from: string, to: string): Decimal | null => {
      if (from === to) return new Decimal(1)

      const direct = bidsMap.get(from + to)
      if (direct) return new Decimal(direct)

      const reverse = bidsMap.get(to + from)
      if (reverse) return new Decimal(1).div(reverse)

      return null
    }

    const allCurrencies = new Set<string>()
    Array.from(bidsMap.keys()).forEach((underlier) => {
      for (let i = 1; i < underlier.length; i += 1) {
        const base = underlier.slice(0, i)
        const quote = underlier.slice(i)
        if (bidsMap.has(base + quote)) {
          allCurrencies.add(base)
          allCurrencies.add(quote)
        }
      }
    })

    const resolveRate = (asset: string): Decimal | null => {
      const direct = findConversionRate(asset, userCurrency)
      if (direct) return direct

      for (const c of Array.from(allCurrencies)) {
        if (c === asset || c === userCurrency) continue
        const leg1 = findConversionRate(asset, c)
        if (!leg1) continue

        const leg2 = findConversionRate(c, userCurrency)
        if (!leg2) continue

        return leg1.mul(leg2)
      }

      const toUsd = findConversionRate(asset, 'USD')
      const usdToUser = findConversionRate('USD', userCurrency)
      if (toUsd && usdToUser) return toUsd.mul(usdToUser)

      return null
    }

    balances.data.forEach(({ asset, amount }) => {
      try {
        const value = new Decimal(amount)
        if (value.isZero() || !value.isFinite()) return

        const rate = resolveRate(asset)
        if (!rate || !rate.isFinite()) return

        const contribution = value.mul(rate)
        totalEquity = totalEquity.plus(contribution)

        // console.debug(`[EQUITY] ${asset}: ${value.toFixed()} × ${rate.toFixed()} = ${contribution.toFixed()}`)
      } catch (e) {
        console.error(`[EQUITY] Failed to parse ${asset}: ${amount}`)
      }
    })

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: userCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(totalEquity.toNumber())
  },
)
